/**
 * The listing name warning on the iPhone card. Apple's public search sends
 * `access-control-allow-origin: *`, so the browser asks it directly and no backend is involved.
 *
 * The search only sees live apps, so a miss proves nothing: this only ever warns, and never says a
 * name is free. Apple's own answer when the name is saved stays final.
 */

/** Says what was seen and what may follow, never that Apple will refuse: the match could be the builder's own app. */
export const NAME_ON_APP_STORE_WARNING =
  'An app with this name is already on the App Store, so Apple may refuse it. You can still continue.';

const comparable = (s: string): string => s.trim().toLowerCase();

/** Whether any search result carries exactly this name. Results come from Apple, so none is trusted to have the expected shape. */
export function nameIsOnAppStore(name: string, results: readonly unknown[]): boolean {
  const wanted = comparable(name);
  if (!wanted) return false;
  return results.some((r) => {
    const trackName = (r as { trackName?: unknown } | null)?.trackName;
    return typeof trackName === 'string' && comparable(trackName) === wanted;
  });
}

const STORES = ['us', 'in'] as const;

/** A warning that arrives long after the builder has moved on is worse than none. */
const SEARCH_TIMEOUT_MS = 5000;

type SearchFetch = (url: string, init?: RequestInit) => Promise<Response>;

/** One per form, because `latest` and `searched` only mean something for the one name field they track. */
export class AppStoreNameCheck {
  taken = false;
  /** Only the newest search may set `taken`: its reply can arrive after a later edit or search. */
  private latest = 0;
  /** The name `taken` speaks for; null once it was cleared. */
  private searched: string | null = null;
  /** Every store answered the search for `searched`, so repeating it would learn nothing. */
  private answered = false;

  // Plain fetch rather than HttpClient: no user token or cookie has any business reaching Apple.
  constructor(
    private readonly fetchFn: SearchFetch = (u, i) => fetch(u, i),
    private readonly timeoutMs: number = SEARCH_TIMEOUT_MS,
  ) {}

  clear(): void {
    this.latest++;
    this.taken = false;
    this.searched = null;
    this.answered = false;
  }

  /**
   * For a name nobody just typed. The form is refilled after every save, and repeating the search
   * then would make the warning blink and spend calls on an answer we already have.
   */
  async checkIfNew(name: string): Promise<void> {
    if (name.trim() !== this.searched) await this.check(name);
  }

  /** A failed search may be tried again, since the builder has no other way to ask. */
  async checkOnLeave(name: string): Promise<void> {
    if (name.trim() === this.searched && this.answered) return;
    await this.check(name);
  }

  /** Never rejects: an outage must not look like a problem with the name, nor get in the way of saving. */
  async check(name: string): Promise<void> {
    this.clear();
    const mine = this.latest;
    const term = name.trim();
    if (!term) return;
    this.searched = term;
    const stores = await Promise.all(STORES.map((country) => this.search(term, country)));
    if (mine !== this.latest) return;
    this.answered = stores.every((results) => results !== null);
    this.taken = stores.some((results) => nameIsOnAppStore(term, results ?? []));
  }

  /** One store's results, or null when it can't answer, so the other store can still warn and the name can be asked again. */
  private async search(term: string, country: string): Promise<unknown[] | null> {
    const url = `https://itunes.apple.com/search?term=${encodeURIComponent(term)}&country=${country}&entity=software&limit=50`;
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), this.timeoutMs);
    try {
      const res = await this.fetchFn(url, { credentials: 'omit', signal: abort.signal });
      if (!res.ok) return null;
      const body = await res.json();
      return Array.isArray(body?.results) ? body.results : null;
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }
}
